import { Injectable, inject, signal } from '@angular/core';
import { DifyBaseService } from './dify.base.service';
import { DifyConfigService } from '@/app/services/dify-config.service';
import { QuickActionStateService } from '@/app/services/quick-action-state.service';
import { AuthService } from '@/app/core/services/auth.service';
import { ChatMessage } from './types';

/** 报告类型：OTI（道路试验）、LP（检车） */
export type ReportType = 'OTI' | 'LP';

/**
 * 语音报报告流程步骤。
 *
 * - `idle`：无进行中的报告（历史已提交或尚未开始），打开对话框时进入"选择类型"创建页
 * - `select-type`：创建页（选择报告类型），尚未开始采集
 * - `chatting`：正在语音/文字采集字段中（补问进行时）
 * - `confirm`：字段已齐，展示可编辑确认卡片
 */
export type VoiceFlowStep = 'idle' | 'select-type' | 'chatting' | 'confirm' | 'completed';

/** Dify 语音报报告会话列表项 */
export interface VoiceConversation {
  id: string;
  reportType: ReportType | null;
  /** 是否已提交（report_id 会话变量非空） */
  submitted: boolean;
  updatedAt: number;
  /** 车辆名称（预填字段） */
  vehicle: string;
  /** 问题简述/描述（collected_fields 的 heading/description） */
  heading: string;
  /** 原始会话 inputs（含 report_type / prefilled_fields，用于恢复会话，避免单独拉接口） */
  inputs: Record<string, unknown>;
}

/**
 * 语音报报告服务。
 *
 * 面向 Driver 的事件报告（Deviation Report）语音采集，对接本地 Dify chatflow。
 *
 * ## 会话闭环设计（何时保留对话 / 何时显示创建页）
 *
 * 语音报报告与通用助手共用同一个 Dify user（邮箱），Dify 的 conversation 按 user 混存，
 * 无法可靠区分"通用查询"与"创建报告"，因此语音报报告**不自动加载 Dify 历史**，
 * 其会话生命周期完全由前端状态驱动：
 *
 * 1. 打开"创建差异报告" → 若 `voiceStep === 'idle'`（无进行中报告）→ 显示创建页（select-type）
 * 2. 选择类型后进入采集中（chatting → confirm）
 * 3. 报告提交成功后 `reset()` → voiceStep 回到 idle → 下次打开显示干净的创建页
 * 4. 中途关闭对话框（未提交）→ service 为 singleton，组件销毁后状态仍保留 →
 *    再次打开时若 voiceStep 非 idle 则**恢复**到之前的对话位置（不丢失进度）
 * 5. 用户主动点击"新建" → `startNewReport()` 立即重置到创建页
 *
 * 这样形成 `创建页 → 采集中 → 确认/提交 → 回到创建页` 的闭环。
 *
 * ## 未提交报告的恢复（防误关丢失）
 *
 * 对话记录已由 Dify 后端持久化（conversation + messages），前端**不额外存 localStorage**。
 * 用户误关浮窗/刷新页面后重新打开"创建差异报告"时，通过 Dify 接口恢复最近一次
 * 语音报告会话：
 * - `/conversations` 按 user 拉取会话，其 `inputs.report_type` 存在即语音报告会话
 * - 恢复 conversation_id + 从 `/messages` 拉回对话记录 + 从 `/variables` 拉回 collected_fields
 */
@Injectable({ providedIn: 'root' })
export class DifyVoiceReportService extends DifyBaseService {
  private readonly configService = inject(DifyConfigService);
  private readonly quickActionState = inject(QuickActionStateService);
  private readonly authService = inject(AuthService);

  /** apiUrl / apiKey 实时读取当前目标（公司/本地） */
  protected get apiUrl(): string {
    return this.configService.voiceReportConfig.apiUrl;
  }
  protected get apiKey(): string {
    return this.configService.voiceReportConfig.apiKey;
  }

  /** 语音报报告不自动加载 Dify 历史，会话由前端状态驱动（见类注释） */
  protected override loadHistoryOnInit = false;

  /**
   * 确保 userEmail 已从 AuthService 填充。
   * 直接通过路由页面进入（不经悬浮球）时，setUser 可能未被调用，userEmail 为空，
   * 会导致会话接口用错误的 `tvms-user`。此方法在构造时自动用真实邮箱补齐。
   */
  private ensureUser(): void {
    if (this.userEmail()) return;
    const profile = this.authService.getCurrentUserProfile();
    if (profile?.email) {
      this.userEmail.set(profile.email);
    }
  }

  /** 当前报告类型（OTI / LP） */
  reportType = signal<ReportType | null>(null);
  /** 预填字段（check-in 车辆信息 + 车辆档案映射） */
  prefilledFields = signal<Record<string, unknown>>({});
  /** 语音报报告流程步骤（提升到 service，组件销毁后仍保留，实现中途关闭再打开恢复） */
  voiceStep = signal<VoiceFlowStep>('idle');
  /** 确认卡片字段（收集到的结构化字段 + 预填字段） */
  confirmFields = signal<Record<string, unknown>>({});
  /** 本次会话是否是从历史 conversation 恢复的（供 UI 展示"已恢复未提交报告"提示） */
  restoredFromDraft = signal(false);

  constructor() {
    super();
    this.ensureUser();
    // 不持久化表单草稿：驾驶员不理解草稿来源，统一由用户从 conversation 列表自行恢复。
    // 历史会话恢复在 restoreConversation() 中完成，并通过 restoredFromDraft 提示用户。
  }

  /** 设置报告类型 */
  setReportType(type: ReportType): void {
    this.reportType.set(type);
  }

  /** 设置预填字段 */
  setPrefilledFields(fields: Record<string, unknown>): void {
    this.prefilledFields.set(fields);
  }

  /**
   * 从 check-in 车辆信息 + 车辆档案构建预填字段。
   * 车辆基础字段不参与语音补问，直接预填。供 FAB 快捷入口与 chat-dialog 复用。
   * @param reportType 报告类型（OTI / LP），作为 testItem 预填
   */
  buildPrefilledFields(reportType: ReportType): Record<string, unknown> {
    const checkIn = this.quickActionState.checkInState()?.vehicle;
    const profile = this.authService.getCurrentUserProfile();
    const fields: Record<string, unknown> = {
      vehicle: checkIn?.generalAttribute || '',
      vin: checkIn?.vinNumber || '',
      driver: profile?.displayName || '',
      testDate: new Date().toISOString().slice(0, 10),
      testItem: reportType,
    };
    // trailer（是否带挂）：不再默认 false。它作为可编辑布尔字段（TRAILER_FIELD），
    // 由驾驶员在确认卡片中明确选择"带挂/不带挂"，避免默认值误导。
    return fields;
  }

  /** 是否可创建报告（已 check-in 车辆） */
  canCreateReport(): boolean {
    return !!this.quickActionState.checkInState()?.vehicle;
  }

  /**
   * 开始一份指定类型的报告：设置类型、预填字段并进入采集中。
   * 供 FAB 的 OTI/LP 快捷入口使用，跳过"选择类型"创建页直接开始采集。
   * 会清空上一份报告的残留对话，确保从干净状态开始。
   */
  startReportOfType(type: ReportType): void {
    this.reportType.set(type);
    const prefilled = this.buildPrefilledFields(type);
    this.prefilledFields.set(prefilled);
    this.confirmFields.set({ ...prefilled });
    this.clearMessages();
    this.startNewConversation();
    this.voiceStep.set('chatting');
  }

  /** 设置流程步骤 */
  setVoiceStep(step: VoiceFlowStep): void {
    this.voiceStep.set(step);
  }

  /** 设置确认卡片字段 */
  setConfirmFields(fields: Record<string, unknown>): void {
    this.confirmFields.set(fields);
  }

  /**
   * 是否还有进行中的报告（用于决定打开对话框时是恢复对话还是显示创建页）。
   * 处于采集中/确认阶段的未提交报告视为"进行中"。
   */
  get hasActiveReport(): boolean {
    return this.voiceStep() === 'chatting' || this.voiceStep() === 'confirm';
  }

  /**
   * 重置报告流程（提交完成后 / 用户主动新建）。
   * 清空所有流程状态与消息，并开启全新会话，回到"无进行中报告"。
   */
  reset(): void {
    this.reportType.set(null);
    this.prefilledFields.set({});
    this.voiceStep.set('idle');
    this.confirmFields.set({});
    this.restoredFromDraft.set(false);
    this.clearMessages();
    this.startNewConversation();
  }

  /**
   * 报告提交完成：重置流程状态但**保留消息**（不清空）。
   * 用于提交成功后在对话中展示"报告已提交"的反馈消息，并回到"无进行中报告"状态，
   * 下一次新建（startNewReport / reset）才会清空。
   */
  completeReport(): void {
    this.reportType.set(null);
    this.prefilledFields.set({});
    this.voiceStep.set('completed');
    this.confirmFields.set({});
  }

  /**
   * 开始一份新报告：立即清空 conversation 界面的所有 UI（消息列表、确认表单、
   * 输入框），并开启全新会话，回到干净的"选择类型"创建页。
   * 确保用户点"新建"后看不到上一份报告的残留内容。
   */
  startNewReport(): void {
    this.reportType.set(null);
    this.prefilledFields.set({});
    this.confirmFields.set({});
    this.restoredFromDraft.set(false);
    this.clearMessages();
    this.startNewConversation();
    this.voiceStep.set('select-type');
  }

  /** 定制 inputs：在用户上下文基础上追加报告类型与预填字段 */
  protected override buildInputs(): Record<string, unknown> {
    return {
      ...super.buildInputs(),
      report_type: this.reportType() || '',
      prefilled_fields: JSON.stringify(this.prefilledFields() || {}),
    };
  }

  /**
   * 获取会话的结构化字段（collected_fields）。
   * Dify 将多轮采集的字段保存在会话变量 collected_fields 中，
   * 前端据此渲染可编辑的确认卡片。
   * @param conversationId 会话 id
   * @param user 用户标识（须与发消息时的 user 一致）
   * @returns 结构化字段对象（已含预填字段）
   */
  /**
   * 拉取该用户的全部语音报报告会话列表（新→旧），并标注提交状态。
   * 通过 `/conversations` 按 user 分页拉取，过滤出 `inputs.report_type`（语音报告会话），
   * 再逐个查 `report_id` 会话变量判断是否已提交。
   *
   * @param user 用户标识
   * @returns 语音报报告会话列表（不含通用助手会话）
   */
  async listConversations(user: string): Promise<VoiceConversation[]> {
    if (!user) return [];
    this.ensureUser();
    const result: VoiceConversation[] = [];
    try {
      const convUrl = `${this.apiUrl}/conversations?user=${encodeURIComponent(user)}&limit=100`;
      const convRes = await fetch(convUrl, {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      });
      if (!convRes.ok) return result;
      const convData = (await convRes.json()) as {
        data?: Array<{
          id: string;
          inputs?: Record<string, unknown>;
          updated_at?: number;
        }>;
      };

      for (const conv of convData.data || []) {
        const inputs = conv.inputs || {};
        const reportType = ((inputs as any)['report_type'] as ReportType) || null;
        if (!reportType) continue; // 跳过通用助手会话

        let vehicle = '';
        let heading = '';
        let submitted = false;

        // 车辆名称来自前端预填（prefilled_fields），存在 conversation 的 inputs 中，
        // 而非 Dify 采集的 collected_fields（Dify 不采集车辆）。
        // 优先从 inputs.prefilled_fields 解析；collected_fields.vehicle 仅作回退。
        try {
          const prefilledRaw = (inputs as any)['prefilled_fields'];
          if (typeof prefilledRaw === 'string') {
            const prefilled = JSON.parse(prefilledRaw) as Record<string, unknown>;
            vehicle = String(prefilled['vehicle'] || '');
          }
        } catch { /* 忽略解析失败 */ }

        // 逐个会话查 collected_fields 与 report_id（POC 阶段会话数量有限，可接受）
        try {
          const varsUrl = `${this.apiUrl}/conversations/${conv.id}/variables?user=${encodeURIComponent(user)}`;
          const varsRes = await fetch(varsUrl, {
            headers: { Authorization: `Bearer ${this.apiKey}` },
          });
          if (varsRes.ok) {
            const varsData = await varsRes.json();
            const variables = varsData.data || [];
            const reportVar = variables.find((v: any) => v.name === 'report_id');
            submitted = !!(reportVar && reportVar.value);
            const collectedVar = variables.find((v: any) => v.name === 'collected_fields');
            if (collectedVar) {
              const fields = this.parseDictValue(collectedVar.value) as Record<string, unknown>;
              if (!vehicle) vehicle = String(fields['vehicle'] || '');
              heading = String(fields['description'] || fields['heading'] || '');
            }
          }
        } catch { /* 忽略单会话失败 */ }

        result.push({
          id: conv.id,
          reportType,
          submitted,
          updatedAt: (conv.updated_at || 0) * 1000,
          vehicle,
          heading,
          inputs,
        });
      }

      result.sort((a, b) => b.updatedAt - a.updatedAt);
    } catch {
      /* 忽略 */
    }
    return result;
  }

  /**
   * 删除指定的 Dify 会话（conversation）。
   * 用于清理脏数据/误创建的会话：`DELETE /conversations/{id}`。
   *
   * @param conversationId 要删除的会话 id
   * @param user 用户标识
   * @returns 是否删除成功
   */
  async deleteConversation(conversationId: string, user: string): Promise<boolean> {
    if (!conversationId || !user) return false;
    try {
      const url = `${this.apiUrl}/conversations/${encodeURIComponent(conversationId)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /**
   * 从 Dify 后端恢复最近一次语音报报告会话（防误关/刷新丢失）。
   * 对话记录本就存储在 Dify，无需本地持久化。
   *
   * 通过 `/conversations` 按 user 拉取最近会话，命中 `inputs.report_type`（语音报告会话）
   * 后恢复：conversation_id、reportType、prefilledFields、对话记录（/messages）、
   * 已采集字段（/variables）。
   *
   * @param user 用户标识
   * @returns 是否成功恢复了一个语音报告会话
   */
  async restoreLastDraft(user: string): Promise<boolean> {
    if (!user || this.voiceStep() === 'chatting' || this.voiceStep() === 'confirm') {
      // 当前已有进行中的会话，无需恢复
      return false;
    }
    try {
      const convUrl = `${this.apiUrl}/conversations?user=${encodeURIComponent(user)}&limit=1`;
      const convRes = await fetch(convUrl, {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      });
      if (!convRes.ok) return false;
      const convData = (await convRes.json()) as { data?: Array<{ id: string; inputs?: Record<string, unknown> }> };
      const lastConv = convData.data?.[0];
      if (!lastConv) return false;
      return await this.restoreConversation(lastConv.id, user, lastConv.inputs || {});
    } catch {
      return false;
    }
  }

  /**
   * 恢复指定 conversation 到当前编辑状态（点击会话列表项时调用）。
   * 会覆盖当前 voiceStep / reportType / messages / confirmFields / conversationId。
   *
   * 注意：Dify 没有 `GET /conversations/{id}` 单查接口，会话的 `inputs`
   * 必须由调用方从会话列表（listConversations）或列表接口传入。
   */
  async restoreConversation(conversationId: string, user: string, inputs?: Record<string, unknown>): Promise<boolean> {
    if (!conversationId) return false;
    try {
      // 仅当调用方未传入 inputs 时，才通过"会话列表"接口获取（取最近一条匹配的）
      if (!inputs) {
        const convUrl = `${this.apiUrl}/conversations?user=${encodeURIComponent(user)}&limit=100`;
        const convRes = await fetch(convUrl, {
          headers: { Authorization: `Bearer ${this.apiKey}` },
        });
        if (!convRes.ok) return false;
        const convData = (await convRes.json()) as { data?: Array<{ id: string; inputs?: Record<string, unknown> }> };
        const found = (convData.data || []).find((c: any) => c.id === conversationId);
        inputs = found?.inputs || {};
      }

      // 只有"语音报报告"会话才恢复（inputs.report_type 存在）
      const reportType = ((inputs as any)['report_type'] as ReportType) || null;
      if (!reportType) return false;

      this.conversationId = conversationId;
      this.reportType.set(reportType);

      // 恢复预填字段
      const prefilledRaw = (inputs as any)['prefilled_fields'];
      let prefilled: Record<string, unknown> = {};
      if (typeof prefilledRaw === 'string') {
        try {
          prefilled = JSON.parse(prefilledRaw) as Record<string, unknown>;
        } catch { /* 忽略 */ }
      }
      this.prefilledFields.set(prefilled);

      // 拉回对话记录
      await this.loadMessagesFromDify(conversationId, user);

      // 拉回已采集字段，决定进入 confirm / completed / chatting
      const fields = await this.getConversationFields(conversationId, user);
      if (fields && Object.keys(fields).length > 0) {
        const desc = String((fields as any).eventDescription || '').trim();
        this.confirmFields.set({ ...prefilled, ...fields });
        // 若该会话的 report_id 非空（已提交），进入 completed（显示"已提交"，防止重复提交）
        const { reportId } = await this.getReportId(conversationId, user);
        if (reportId) {
          this.voiceStep.set('completed');
        } else {
          this.voiceStep.set(desc ? 'confirm' : 'chatting');
        }
      } else {
        this.voiceStep.set('chatting');
      }

      this.restoredFromDraft.set(true);
      return true;
    } catch {
      return false;
    }
  }

  /** 从 Dify `/messages` 拉取某会话的对话记录到 messages */
  private async loadMessagesFromDify(conversationId: string, user: string): Promise<void> {
    try {
      const msgUrl = `${this.apiUrl}/messages?conversation_id=${conversationId}&user=${encodeURIComponent(user)}`;
      const msgRes = await fetch(msgUrl, {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      });
      if (!msgRes.ok) return;
      const msgData = (await msgRes.json()) as { data?: Array<{ id: string; query: string; answer: string; created_at: number }> };
      const history: ChatMessage[] = [];
      for (const m of msgData.data || []) {
        history.push({
          id: `${m.id}-q`,
          role: 'user',
          content: m.query,
          timestamp: m.created_at * 1000,
          status: 'done',
        });
        history.push({
          id: `${m.id}-a`,
          role: 'assistant',
          content: m.answer,
          timestamp: m.created_at * 1000,
          status: 'done',
        });
      }
      this.messages.set(history);
    } catch {
      /* 忽略 */
    }
  }

  async getConversationFields(conversationId: string, user: string): Promise<Record<string, unknown>> {
    if (!conversationId) return {};
    try {
      const response = await fetch(
        `${this.apiUrl}/conversations/${conversationId}/variables?user=${encodeURIComponent(user)}`,
        { headers: { Authorization: `Bearer ${this.apiKey}` } }
      );
      if (!response.ok) return {};
      const data = await response.json();
      const collected = (data?.data || []).find((v: any) => v.name === 'collected_fields');
      if (!collected) return {};
      return this.parseDictValue(collected.value) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  /**
   * 获取某会话的 report_id 会话变量值（非空即代表该会话已提交）。
   * @returns `{ reportId: string; variableId: string }` 或 `{ reportId: ''; variableId: '' }`
   */
  async getReportId(conversationId: string, user: string): Promise<{ reportId: string; variableId: string }> {
    if (!conversationId) return { reportId: '', variableId: '' };
    try {
      const response = await fetch(
        `${this.apiUrl}/conversations/${conversationId}/variables?user=${encodeURIComponent(user)}`,
        { headers: { Authorization: `Bearer ${this.apiKey}` } }
      );
      if (!response.ok) return { reportId: '', variableId: '' };
      const data = await response.json();
      const reportVar = (data?.data || []).find((v: any) => v.name === 'report_id');
      if (!reportVar) return { reportId: '', variableId: '' };
      return { reportId: String(reportVar.value || ''), variableId: reportVar.id };
    } catch {
      return { reportId: '', variableId: '' };
    }
  }

  /**
   * 报告提交成功后，把生成的 report_id 写入该会话的 report_id 会话变量。
   * 用于在 conversation 里维护"已提交"状态，跨设备/刷新均可判断，防止重复提交。
   */
  async setReportId(conversationId: string | null, user: string, reportId: string): Promise<void> {
    if (!conversationId || !reportId) return;
    try {
      const { variableId } = await this.getReportId(conversationId, user);
      if (!variableId) return;
      await fetch(
        `${this.apiUrl}/conversations/${conversationId}/variables/${variableId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ value: reportId, user }),
        }
      );
    } catch {
      /* 写入失败不阻塞提交流程 */
    }
  }

  /**
   * 解析 Dify 会话变量返回的 Python dict 字符串。
   * 形如 "{'vehicle': '测试车A', 'vin': 'LFV3A23K8P3123456', '__skipped': []}"。
   * 也兼容标准 JSON。
   */
  private parseDictValue(value: string): unknown {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    // 兼容标准 JSON
    if (trimmed.startsWith('{')) {
      try {
        return JSON.parse(trimmed);
      } catch {
        /* 走 Python dict 解析 */
      }
    }
    // Python dict 解析：把单引号替换为双引号，并把 Python 布尔/None 转成合法 JSON，
    // 否则 Dify 会写 `trailer: False`（大写）导致 JSON.parse 失败、整个字段解析为空。
    try {
      const json = trimmed
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null')
        .replace(/([{,]\s*)"/g, '$1"');
      return JSON.parse(json);
    } catch {
      return {};
    }
  }
}
